import React from "react";
//#region types
import { AuthEnums, StyleEnums } from "models/enums";
import { ActionButton } from "models/propTypes/commonPropTypes";
import { TranslationKeys } from "translations";
import { WeeklyViewFooterProps } from "models/propTypes/timeReportingPropTypes";
import { WintSuperkollAPIModelTimeReporting } from "wint-api-client/dist/types";
//#endregion
//#region components
import { PageFooter } from "components/common";
import { useTimeReportingService } from "serviceHooks";
import WeeklyTimeReportMoreActions from "../weeklyTimeReport/actions/moreActions";
import useTimeReportingEventHandlers from "../forms/useTimeReportingEventHandlers";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { CompanyService } from "services";

const WeeklyViewFooter: React.FC<WeeklyViewFooterProps> = (props) => {
  const timeReportingService = useTimeReportingService();
  const { weekSegments, employeeId, reloadHandler, isLastWeek, monthStatus, hasUnSavedChanges } = props;

  const weekStart = React.useMemo(() => {
    return weekSegments ? weekSegments[0].weekSegmentStart : undefined;
  }, [weekSegments]);

  const onLockWeekClick = async (week: WintSuperkollAPIModelTimeReporting.WeekSegment, lock: boolean) => {
    const model: WintSuperkollAPIModelTimeReporting.UpdateStatusByDateRange = {
      employeeId: employeeId,
      startDate: week.weekSegmentStart,
      endDate: week.weekSegmentEnd,
      status: lock
        ? WintSuperkollAPIModelTimeReporting.TimeReportStatus.Locked
        : WintSuperkollAPIModelTimeReporting.TimeReportStatus.Open,
    };
    lock && props.onCreateOrUpdateWeek && (await props.onCreateOrUpdateWeek());
    return timeReportingService.updateStatus(model);
  };

  const changeMonthStatus = async (lock: boolean) => {
    if (weekSegments) {
      const weekStartDate = new Date(weekSegments[0].weekSegmentStart);
      const monthStartDate = DateHelper.getFirstDayOfMonth(weekStartDate);
      const monthEndDate = DateHelper.getLastDayOfMonth(weekStartDate);
      const model: WintSuperkollAPIModelTimeReporting.UpdateStatusByDateRange = {
        employeeId: employeeId,
        startDate: monthStartDate,
        endDate: monthEndDate,
        status: lock
          ? WintSuperkollAPIModelTimeReporting.TimeReportStatus.Locked
          : WintSuperkollAPIModelTimeReporting.TimeReportStatus.Open,
      };

      return timeReportingService.updateStatus(model);
    }
  };

  const { onLockMonthClick } = useTimeReportingEventHandlers(changeMonthStatus.bind(null, true), reloadHandler);

  const lockWeekAction = (week: WintSuperkollAPIModelTimeReporting.WeekSegment) => {
    return {
      text: `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.LockWeek)} `,
      onClick: onLockWeekClick.bind(null, week, true),
      type: StyleEnums.DisplayType.Success,
      isDisabled:
        !props.isValid || week.weekSegmentStatus === WintSuperkollAPIModelTimeReporting.TimeReportStatus.Attested,
      successMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.LockWeekSuccess),
      onActionSuccess: reloadHandler,
    };
  };

  const unLockWeekAction = (week: WintSuperkollAPIModelTimeReporting.WeekSegment, showDates?: boolean) => {
    return {
      text: `${LocalizationHelperInstance.getFormattedMessage(
        showDates ? TranslationKeys.TimeReporting.UnLock : TranslationKeys.TimeReporting.UnLockWeek
      )} ${showDates ? timeReportingService.getWeekDateDisplay(week) : ""}`,
      onClick: onLockWeekClick.bind(null, week, false),
      type: StyleEnums.DisplayType.Success,
      isDisabled:
        !props.isValid ||
        (week.weekSegmentStatus === WintSuperkollAPIModelTimeReporting.TimeReportStatus.Attested &&
          !CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.SalaryAdmin, true)),
      successMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.UnLockWeekSuccess),
      onActionSuccess: reloadHandler,
    };
  };

  const lockMonthAction = {
    text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.LockMonth),
    onClick: onLockMonthClick.bind(null, employeeId, weekStart, props.onCreateOrUpdateWeek),
    type: StyleEnums.DisplayType.Success,
    isDisabled: !props.isValid,
  };

  const getActions = React.useMemo(() => {
    const actions: ActionButton[] = [];

    if (weekSegments) {
      if (!isLastWeek) {
        const weekSegment = weekSegments[0];
        const displayLock: boolean | undefined = timeReportingService.getDisplayLockStatus(
          weekSegment.weekSegmentStatus
        );
        if (displayLock !== undefined) {
          if (displayLock) {
            actions.push(lockWeekAction(weekSegment));
          } else {
            actions.push(unLockWeekAction(weekSegment));
          }
        }
      } else {
        const displayLock: boolean | undefined =
          monthStatus !== undefined ? timeReportingService.getDisplayLockStatus(monthStatus) : undefined;
        if (displayLock !== undefined) {
          if (displayLock) {
            actions.push(lockMonthAction);
          } else {
            if (weekSegments.length === 2) {
              const nextWeekDisplayLock = timeReportingService.getDisplayLockStatus(weekSegments[1].weekSegmentStatus);
              if (nextWeekDisplayLock) {
                actions.push(lockWeekAction(weekSegments[1]));
              } else {
                actions.push(unLockWeekAction(weekSegments[1], true));
              }
            } else {
              actions.push(unLockWeekAction(weekSegments[0]));
            }
          }
        }
      }

      actions.push({
        text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Save),
        onClick: props.onCreateOrUpdateWeek,
        type: StyleEnums.DisplayType.Info,
        isDisabled: !props.isValid || !hasUnSavedChanges,
        successMessage: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.TimeReporting.TimeReportedSuccess
        ),
        onActionSuccess: reloadHandler,
      });
    }

    return actions;
  }, [monthStatus, props.isValid, isLastWeek, hasUnSavedChanges]);

  return (
    <PageFooter
      actions={getActions}
      moreActions={<WeeklyTimeReportMoreActions {...props}></WeeklyTimeReportMoreActions>}
    />
  );
};

export default WeeklyViewFooter;
